import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

// Theme color categories for organized display
export interface ThemeColors {
  // Core colors
  background: string;
  foreground: string;
  
  // Primary brand colors
  primary: string;
  primaryForeground: string;
  
  // Secondary colors
  secondary: string;
  secondaryForeground: string;
  
  // Accent colors
  accent: string;
  accentForeground: string;
  
  // Card and popover
  card: string;
  cardForeground: string;
  popover: string;
  popoverForeground: string;
  
  // Muted colors
  muted: string;
  mutedForeground: string;
  
  // Destructive/error
  destructive: string;
  destructiveForeground: string;
  
  // UI elements
  border: string;
  input: string;
  ring: string;
  
  // Status colors
  success: string;
  warning: string;
  info: string;
  live: string;
  
  // Sidebar colors
  sidebarBackground: string;
  sidebarForeground: string;
  sidebarPrimary: string;
  sidebarPrimaryForeground: string;
  sidebarAccent: string;
  sidebarAccentForeground: string;
  sidebarBorder: string;
  sidebarRing: string;
}

export interface BrandingSettings {
  // Logos
  site_logo_url: string | null;
  site_logo_mobile_url: string | null;
  
  // Favicon
  favicon_source_url: string;
  favicon_16_url: string;
  favicon_32_url: string;
  favicon_180_url: string;
  favicon_192_url: string;
  favicon_512_url: string;
  
  // PWA/Meta
  site_name: string;
  theme_color: string;
  background_color: string;
  
  // Border radius
  radius: string;
  
  // Light mode colors
  colors_light: ThemeColors;
  
  // Dark mode colors
  colors_dark: ThemeColors;
}

// Default light mode colors (from index.css :root)
const DEFAULT_LIGHT_COLORS: ThemeColors = {
  background: '220 20% 97%',
  foreground: '220 20% 10%',
  primary: '145 70% 40%',
  primaryForeground: '0 0% 100%',
  secondary: '220 15% 92%',
  secondaryForeground: '220 20% 15%',
  accent: '145 60% 45%',
  accentForeground: '0 0% 100%',
  card: '0 0% 100%',
  cardForeground: '220 20% 10%',
  popover: '0 0% 100%',
  popoverForeground: '220 20% 10%',
  muted: '220 15% 94%',
  mutedForeground: '220 10% 45%',
  destructive: '0 72% 51%',
  destructiveForeground: '0 0% 100%',
  border: '220 15% 88%',
  input: '220 15% 88%',
  ring: '145 70% 40%',
  success: '145 70% 40%',
  warning: '45 93% 47%',
  info: '210 100% 50%',
  live: '0 84% 60%',
  sidebarBackground: '0 0% 98%',
  sidebarForeground: '220 10% 30%',
  sidebarPrimary: '145 70% 40%',
  sidebarPrimaryForeground: '0 0% 100%',
  sidebarAccent: '145 60% 95%',
  sidebarAccentForeground: '145 70% 25%',
  sidebarBorder: '220 15% 90%',
  sidebarRing: '145 70% 40%',
};

// Default dark mode colors (from index.css .dark)
const DEFAULT_DARK_COLORS: ThemeColors = {
  background: '220 25% 8%',
  foreground: '220 10% 95%',
  primary: '145 65% 45%',
  primaryForeground: '220 25% 8%',
  secondary: '220 20% 18%',
  secondaryForeground: '220 10% 90%',
  accent: '145 60% 40%',
  accentForeground: '0 0% 100%',
  card: '220 25% 12%',
  cardForeground: '220 10% 95%',
  popover: '220 25% 12%',
  popoverForeground: '220 10% 95%',
  muted: '220 20% 16%',
  mutedForeground: '220 10% 55%',
  destructive: '0 62% 50%',
  destructiveForeground: '0 0% 100%',
  border: '220 20% 18%',
  input: '220 20% 18%',
  ring: '145 65% 45%',
  success: '145 65% 45%',
  warning: '45 93% 50%',
  info: '210 100% 55%',
  live: '0 72% 55%',
  sidebarBackground: '220 25% 10%',
  sidebarForeground: '220 10% 85%',
  sidebarPrimary: '145 65% 45%',
  sidebarPrimaryForeground: '220 25% 8%',
  sidebarAccent: '145 40% 15%',
  sidebarAccentForeground: '145 60% 60%',
  sidebarBorder: '220 20% 15%',
  sidebarRing: '145 65% 45%',
};

const DEFAULT_BRANDING: BrandingSettings = {
  site_logo_url: '/branding/logo-desktop.png',
  site_logo_mobile_url: '/branding/icon-master.png',
  favicon_source_url: '/branding/icon-master.png',
  favicon_16_url: '/favicons/favicon-16x16.png',
  favicon_32_url: '/favicons/favicon-32x32.png',
  favicon_180_url: '/favicons/apple-touch-icon.png',
  favicon_192_url: '/favicons/android-chrome-192x192.png',
  favicon_512_url: '/favicons/android-chrome-512x512.png',
  site_name: 'WinWGo',
  theme_color: '#22c55e',
  background_color: '#09090b',
  radius: '0.75rem',
  colors_light: DEFAULT_LIGHT_COLORS,
  colors_dark: DEFAULT_DARK_COLORS,
};

async function fetchBranding(): Promise<BrandingSettings> {
  const { data, error } = await supabase
    .from('app_settings')
    .select('value')
    .eq('key', 'branding')
    .single();

  if (error || !data?.value) {
    return DEFAULT_BRANDING;
  }

  const value = data.value as Partial<BrandingSettings>;
  
  return {
    ...DEFAULT_BRANDING,
    ...value,
    colors_light: {
      ...DEFAULT_LIGHT_COLORS,
      ...(value.colors_light || {}),
    },
    colors_dark: {
      ...DEFAULT_DARK_COLORS,
      ...(value.colors_dark || {}),
    },
  };
}

/**
 * Hook to fetch branding settings from the database
 */
export function useBranding() {
  const query = useQuery({
    queryKey: ['branding'],
    queryFn: fetchBranding,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
    placeholderData: DEFAULT_BRANDING, // Prevents flash by providing immediate data
  });

  return {
    branding: query.data ?? DEFAULT_BRANDING,
    isLoading: query.isLoading && !query.isPlaceholderData,
    error: query.error,
  };
}

/**
 * Hook to update favicon and theme meta tags dynamically based on branding settings
 * Also applies CSS custom properties for site-wide theming
 */
export function useDynamicFavicon() {
  const { branding } = useBranding();

  useEffect(() => {
    if (!branding) return;

    // Cache-busting version for favicon updates
    const FAVICON_VERSION = '20260208';
    
    // Helper to add cache-busting query param
    const withVersion = (url: string) => {
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}v=${FAVICON_VERSION}`;
    };

    // Update favicon links - using new /favicons/ directory structure
    const updateLink = (selector: string, href: string, sizes?: string) => {
      const link = document.querySelector(selector) as HTMLLinkElement | null;
      if (link) {
        link.href = withVersion(href);
        if (sizes) link.sizes.value = sizes;
      }
    };

    // Update all favicon references with cache-busting
    updateLink('link[rel="icon"][sizes="16x16"]', branding.favicon_16_url, '16x16');
    updateLink('link[rel="icon"][sizes="32x32"]', branding.favicon_32_url, '32x32');
    updateLink('link[rel="icon"][sizes="192x192"]', branding.favicon_192_url, '192x192');
    updateLink('link[rel="icon"][sizes="512x512"]', branding.favicon_512_url, '512x512');
    updateLink('link[rel="apple-touch-icon"]', branding.favicon_180_url, '180x180');
    
    // Update ICO favicon (for Google Search) with cache-busting
    const icoLink = document.querySelector('link[rel="shortcut icon"]') as HTMLLinkElement | null;
    if (icoLink) {
      icoLink.href = withVersion('/favicon.ico');
    }
    
    // Update main icon link with cache-busting
    const mainIcon = document.querySelector('link[rel="icon"][type="image/x-icon"]') as HTMLLinkElement | null;
    if (mainIcon) {
      mainIcon.href = withVersion('/favicon.ico');
    }

    // Update theme color meta tags
    const updateMeta = (selector: string, content: string) => {
      const meta = document.querySelector(selector) as HTMLMetaElement | null;
      if (meta) {
        meta.content = content;
      }
    };

    updateMeta('meta[name="theme-color"][media="(prefers-color-scheme: light)"]', branding.theme_color);
    updateMeta('meta[name="theme-color"][media="(prefers-color-scheme: dark)"]', branding.theme_color);
    updateMeta('meta[name="msapplication-TileColor"]', branding.theme_color);

    // Apply CSS custom properties for theming
    applyThemeColors(branding);

  }, [branding]);
}

/**
 * Apply theme colors to CSS custom properties
 */
function applyThemeColors(branding: BrandingSettings) {
  const root = document.documentElement;
  const isDark = root.classList.contains('dark');
  const colors = isDark ? branding.colors_dark : branding.colors_light;
  
  // Set radius
  root.style.setProperty('--radius', branding.radius);

  // Helper to set CSS variable
  const setColor = (name: string, value: string) => {
    // Only set if not the default (allows CSS fallback to work)
    if (value) {
      root.style.setProperty(`--${name}`, value);
    }
  };

  // Apply all color variables
  setColor('background', colors.background);
  setColor('foreground', colors.foreground);
  setColor('primary', colors.primary);
  setColor('primary-foreground', colors.primaryForeground);
  setColor('secondary', colors.secondary);
  setColor('secondary-foreground', colors.secondaryForeground);
  setColor('accent', colors.accent);
  setColor('accent-foreground', colors.accentForeground);
  setColor('card', colors.card);
  setColor('card-foreground', colors.cardForeground);
  setColor('popover', colors.popover);
  setColor('popover-foreground', colors.popoverForeground);
  setColor('muted', colors.muted);
  setColor('muted-foreground', colors.mutedForeground);
  setColor('destructive', colors.destructive);
  setColor('destructive-foreground', colors.destructiveForeground);
  setColor('border', colors.border);
  setColor('input', colors.input);
  setColor('ring', colors.ring);
  setColor('success', colors.success);
  setColor('warning', colors.warning);
  setColor('info', colors.info);
  setColor('live', colors.live);
  setColor('sidebar-background', colors.sidebarBackground);
  setColor('sidebar-foreground', colors.sidebarForeground);
  setColor('sidebar-primary', colors.sidebarPrimary);
  setColor('sidebar-primary-foreground', colors.sidebarPrimaryForeground);
  setColor('sidebar-accent', colors.sidebarAccent);
  setColor('sidebar-accent-foreground', colors.sidebarAccentForeground);
  setColor('sidebar-border', colors.sidebarBorder);
  setColor('sidebar-ring', colors.sidebarRing);
}

// Color category definitions for UI organization
export const COLOR_CATEGORIES = [
  {
    id: 'core',
    label: 'Core Colors',
    description: 'Background and text colors',
    colors: [
      { key: 'background', label: 'Background', description: 'Main page background' },
      { key: 'foreground', label: 'Foreground', description: 'Main text color' },
    ],
  },
  {
    id: 'brand',
    label: 'Brand Colors',
    description: 'Primary brand identity',
    colors: [
      { key: 'primary', label: 'Primary', description: 'Main brand color (buttons, links)' },
      { key: 'primaryForeground', label: 'Primary Foreground', description: 'Text on primary' },
      { key: 'accent', label: 'Accent', description: 'Secondary brand accent' },
      { key: 'accentForeground', label: 'Accent Foreground', description: 'Text on accent' },
    ],
  },
  {
    id: 'surfaces',
    label: 'Surfaces',
    description: 'Cards, popovers, and containers',
    colors: [
      { key: 'card', label: 'Card', description: 'Card backgrounds' },
      { key: 'cardForeground', label: 'Card Foreground', description: 'Card text' },
      { key: 'popover', label: 'Popover', description: 'Popover/dropdown backgrounds' },
      { key: 'popoverForeground', label: 'Popover Foreground', description: 'Popover text' },
    ],
  },
  {
    id: 'secondary',
    label: 'Secondary & Muted',
    description: 'Secondary UI elements',
    colors: [
      { key: 'secondary', label: 'Secondary', description: 'Secondary button backgrounds' },
      { key: 'secondaryForeground', label: 'Secondary Foreground', description: 'Secondary text' },
      { key: 'muted', label: 'Muted', description: 'Muted/disabled backgrounds' },
      { key: 'mutedForeground', label: 'Muted Foreground', description: 'Muted text' },
    ],
  },
  {
    id: 'ui',
    label: 'UI Elements',
    description: 'Borders, inputs, and focus rings',
    colors: [
      { key: 'border', label: 'Border', description: 'Border color' },
      { key: 'input', label: 'Input', description: 'Input border color' },
      { key: 'ring', label: 'Ring', description: 'Focus ring color' },
    ],
  },
  {
    id: 'status',
    label: 'Status Colors',
    description: 'Feedback and status indicators',
    colors: [
      { key: 'success', label: 'Success', description: 'Success states' },
      { key: 'warning', label: 'Warning', description: 'Warning states' },
      { key: 'info', label: 'Info', description: 'Information states' },
      { key: 'live', label: 'Live', description: 'Live/active indicator' },
      { key: 'destructive', label: 'Destructive', description: 'Error/danger states' },
      { key: 'destructiveForeground', label: 'Destructive Foreground', description: 'Text on error' },
    ],
  },
  {
    id: 'sidebar',
    label: 'Sidebar Colors',
    description: 'Admin sidebar theming',
    colors: [
      { key: 'sidebarBackground', label: 'Background', description: 'Sidebar background' },
      { key: 'sidebarForeground', label: 'Foreground', description: 'Sidebar text' },
      { key: 'sidebarPrimary', label: 'Primary', description: 'Active item' },
      { key: 'sidebarPrimaryForeground', label: 'Primary Foreground', description: 'Active item text' },
      { key: 'sidebarAccent', label: 'Accent', description: 'Hover state' },
      { key: 'sidebarAccentForeground', label: 'Accent Foreground', description: 'Hover text' },
      { key: 'sidebarBorder', label: 'Border', description: 'Sidebar borders' },
      { key: 'sidebarRing', label: 'Ring', description: 'Sidebar focus ring' },
    ],
  },
] as const;

export { DEFAULT_BRANDING, DEFAULT_LIGHT_COLORS, DEFAULT_DARK_COLORS };
